import React, { useState, useRef } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { Button } from '../../../../components/ui/button';
import { IHandbookDocument } from 'core/interfaces/handbookInterface';
import {
  getHandbookOwnerType,
  replaceHandbookDocument,
} from 'core/API/handbookDocuments';
import styles from '../SchedulerSetting.module.scss';

interface HandbookEditDocumentProps {
	document: IHandbookDocument;
	existingNames: string[];
	handleBackClick?: () => void;
	onDocumentUpdated?: () => void;
}

const HandbookEditDocument: React.FC<HandbookEditDocumentProps> = ({
	document,
	existingNames,
	handleBackClick,
	onDocumentUpdated,
}) => {
	const [name, setName] = useState(document.name);
	const ownerType = getHandbookOwnerType(document.workplace);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const validateFile = (file: File): string | null => {
		if (file.type !== 'application/pdf') return 'Only PDF files are allowed';
		if (file.size > 10 * 1024 * 1024) return 'File size must be less than 10MB';
		return null;
	};

	const handleFileAccepted = (file: File) => {
		const validationError = validateFile(file);
		if (validationError) {
			setError(validationError);
			setSelectedFile(null);
			return;
		}
		setSelectedFile(file);
		setError(null);
	};

	const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) handleFileAccepted(file);
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = () => setIsDragging(false);

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);
		const file = event.dataTransfer.files?.[0];
		if (file) handleFileAccepted(file);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!document.id) {
			setError('Document ID is missing');
			return;
		}

		const trimmedName = name.trim();

		if (!trimmedName) {
			setError('Document name is required');
			return;
		}

		if (trimmedName !== document.name && existingNames.map((n) => n.toLowerCase()).includes(trimmedName.toLowerCase())) {
			setError(`A document named "${trimmedName}" already exists. Names must be unique.`);
			return;
		}

		if (!selectedFile) {
			setError('Please select a PDF file');
			return;
		}

		setIsSubmitting(true);
		try {
			await replaceHandbookDocument(document.id, selectedFile, trimmedName);
			onDocumentUpdated?.();
			setTimeout(() => handleBackClick?.(), 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to update document');
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick}>
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting || !selectedFile}
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit Document</h1>
					<p style={{ color: '#666', fontSize: '0.875rem', margin: '4px 0 0' }}>{document.name}</p>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && (
					<div style={{ color: '#d32f2f', marginBottom: 16 }} role="alert">
						{error}
					</div>
				)}

				<FormControl component="fieldset" margin="normal" fullWidth disabled style={{ marginBottom: 16 }}>
					<FormLabel component="legend">Ownership Type</FormLabel>
					<RadioGroup row name="ownerType" value={ownerType}>
						<FormControlLabel value="workplace" control={<Radio />} label="Workplace" />
						<FormControlLabel value="organization" control={<Radio />} label="Organization" />
					</RadioGroup>
				</FormControl>

				<TextField
					label="Document Name"
					name="name"
					value={name}
					onChange={(e) => {
						setName(e.target.value);
						setError(null);
					}}
					fullWidth
					required
					style={{ marginBottom: 16 }}
					inputProps={{ maxLength: 200 }}
				/>

				<div
					role="button"
					tabIndex={0}
					aria-label="Upload PDF file — click or drag and drop"
					style={{
						border: isDragging
							? '2px dashed #446388'
							: selectedFile
								? '2px dashed #4caf50'
								: '2px dashed #ccc',
						borderRadius: 8,
						padding: 24,
						textAlign: 'center',
						marginBottom: 16,
						cursor: 'pointer',
						backgroundColor: isDragging ? '#eef2f7' : '#fafafa',
						transition: 'border-color 0.2s, background-color 0.2s',
					}}
					onClick={() => fileInputRef.current?.click()}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
					}}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					<input
						type="file"
						ref={fileInputRef}
						accept=".pdf"
						style={{ display: 'none' }}
						onChange={handleFileInputChange}
						aria-label="Select PDF file"
					/>
					<CloudUpload style={{ fontSize: 48, color: '#999', marginBottom: 8 }} />
					<div style={{ marginBottom: 4 }}>
						{selectedFile ? selectedFile.name : 'Click or drag & drop a PDF file here'}
					</div>
					<div style={{ fontSize: '0.75rem', color: '#999' }}>
						Maximum file size: 10MB | Format: PDF only
					</div>
				</div>
			</form>
		</div>
	);
};

export default HandbookEditDocument;
